/**
 * SimCard (DE-266) — Onomondo SIM quota/usage for one device.
 *
 * Deliberately a sibling of DeviceStatus rather than a row in ModemCard:
 * DeviceStatus renders purely from the shadow prop with no network fetches,
 * while this card polls the admin Lambda (60 s — vendor data behind a 60 s
 * server cache; the 5 s device poll cadence would only reread that cache).
 *
 * The fleet is mixed-carrier, so "Not an Onomondo SIM" (dev unit = Telia) and
 * "not in our Onomondo org" are rendered as calm labels, never as errors.
 * The limit history is the device's own event feed filtered to
 * sim_limit_change — the audit events the admin Lambda publishes on
 * dt/<thing>/events land there via the standard dispatcher path.
 */
import { useState } from 'react';
import {
  useDeviceEvents,
  useMe,
  useSimStatus,
  type DeviceSimStatus,
  type SimQuotaSummary,
} from '../api/hooks';
import { fmtAgo, fmtBytes } from './format';
import { SimTopUpDialog } from './SimTopUpDialog';

function quotaTone(sim: SimQuotaSummary): 'ok' | 'warn' | 'blocked' {
  const q = sim.quota;
  if (!q) return 'ok';
  if (q.blocked) return 'blocked';
  const nearAlert = q.alertThreshold != null && q.usedBytes >= q.alertThreshold;
  const nearCap = q.remainingBytes < q.totalBytes * 0.2;
  return nearAlert || nearCap ? 'warn' : 'ok';
}

function QuotaBar({ sim }: { sim: SimQuotaSummary }) {
  const q = sim.quota!;
  const pct = Math.min(100, Math.round((q.usedBytes / q.totalBytes) * 100));
  const tone = quotaTone(sim);
  const barClass =
    tone === 'blocked' ? 'bg-warn' : tone === 'warn' ? 'bg-warn/60' : 'bg-accent';
  return (
    <div>
      <div className="h-2 rounded-full bg-soft overflow-hidden">
        <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="flex justify-between text-xs mt-1">
        <span className="tabular-nums">
          {fmtBytes(q.usedBytes)} of {fmtBytes(q.totalBytes)} used ({pct}%)
        </span>
        <span className={tone === 'ok' ? 'text-muted' : 'text-warn font-medium'}>
          {q.blocked ? 'cap reached — data blocked' : `${fmtBytes(q.remainingBytes)} left`}
        </span>
      </div>
    </div>
  );
}

const STATE_LABEL: Record<string, string> = {
  'no-iccid': 'No SIM reported yet.',
  'not-onomondo': 'Not an Onomondo SIM — managed outside the Portal.',
  'sim-not-found': 'SIM is not in our Onomondo organisation.',
  'not-configured': 'SIM integration not configured (missing Onomondo API key).',
  'vendor-rate-limit': 'Onomondo rate limit hit — retries shortly.',
  'vendor-error': 'Onomondo unreachable right now.',
  'not-visible': 'Not visible to your tenant.',
};

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-3 text-xs py-0.5">
      <span className="text-muted w-32 shrink-0">{label}</span>
      <span className={mono ? 'font-mono' : undefined}>{value}</span>
    </div>
  );
}

export function SimCard({ thing }: { thing: string }) {
  const me = useMe();
  const status = useSimStatus(thing);
  const events = useDeviceEvents(thing);
  const [dialogOpen, setDialogOpen] = useState(false);
  const isSuperAdmin = me.data?.role === 'super-admin';

  const d: DeviceSimStatus | undefined = status.data;
  const sim = d?.sim ?? null;
  const limitHistory = (events.data?.events ?? []).filter(
    (e) => e.event_type === 'sim_limit_change',
  );

  return (
    <section className="card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-medium text-sm">SIM data quota</h3>
        {sim && isSuperAdmin && (
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="text-accent hover:underline text-xs"
          >
            {sim.quota ? 'Raise limit…' : 'Set limit…'}
          </button>
        )}
      </div>

      {status.isLoading && <p className="text-xs text-muted">Loading…</p>}
      {status.isError && (
        <p className="text-xs text-muted">SIM status unavailable right now.</p>
      )}

      {d && d.error && (
        <p className="text-xs text-muted">
          {STATE_LABEL[d.error] ?? d.error}
          {d.iccid && (
            <>
              {' '}
              <span className="font-mono">({d.iccid})</span>
            </>
          )}
        </p>
      )}

      {sim && (
        <div className="space-y-2">
          {sim.quota ? (
            <QuotaBar sim={sim} />
          ) : (
            <p className="text-xs text-muted">
              No data cap set — usage-based billing only (uncapped).
            </p>
          )}
          <div>
            {sim.quota && (
              <>
                <Row label="limit period" value={sim.quota.limitType} />
                {sim.quota.resetsAt && (
                  <Row
                    label="resets"
                    value={new Date(sim.quota.resetsAt).toLocaleString()}
                  />
                )}
              </>
            )}
            <Row label="Onomondo SIM" value={sim.simId} mono />
            {sim.label && <Row label="label" value={sim.label} />}
            <Row
              label="carrier link"
              value={
                sim.online
                  ? 'online'
                  : sim.lastOnlineAt
                    ? `offline — last online ${fmtAgo(sim.lastOnlineAt)}`
                    : 'offline'
              }
            />
          </div>
        </div>
      )}

      {limitHistory.length > 0 && (
        <div className="mt-3">
          <h4 className="text-xs font-medium text-muted mb-1">Limit history</h4>
          <div className="max-h-28 overflow-y-auto">
            {limitHistory.map((e) => {
              const data = e.data as {
                actor?: string;
                prev_total?: number | null;
                new_total?: number | null;
                reason?: string | null;
              };
              return (
                <div key={e.event_id} className="text-xs py-0.5 flex gap-2 items-baseline">
                  <span className="text-muted tabular-nums whitespace-nowrap">
                    {new Date(e.ts).toLocaleDateString()}
                  </span>
                  <span>
                    {data.prev_total != null ? fmtBytes(data.prev_total) : 'uncapped'}
                    {' → '}
                    {data.new_total != null ? fmtBytes(data.new_total) : 'uncapped'}
                    {data.actor && <span className="text-muted"> · {data.actor}</span>}
                    {data.reason && <span className="text-muted"> — {data.reason}</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {d?.sim && (
        <p className="text-[10px] text-muted mt-2">Fetched {fmtAgo(d.sim.fetchedAt)}</p>
      )}

      {dialogOpen && sim && (
        <SimTopUpDialog
          target={{ mode: 'single', thing, sim }}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </section>
  );
}
